<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
<div
    v-if="pack" class="custom-dropdown" :class="{error: error}"
    :tabindex="tabindex"
    v-on:blur="open = false"
>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
    <div
        class="selected paragraph regular"
        :class="{open: open}"
        tabindex="0"
        v-on:click="open = !open"
        v-on:keydown.enter="open = !open"
    >
        {{ labelText && labelText }} {{ selected.text }}
        <NuxtImg class="arrow-icon" src="/images/icons/platforms/chevron.svg" alt="" aria-hidden />
    </div>

    <div v-if="pack" class="items" :class="{selectHide: !open}">
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <ClientOnly>
            <div
                v-for="(option, i) of options"
                :key="i"
                class="item paragraph external-storefront"
                tabindex="0"
                :class="[option.vendor, getProductClass(pack.slug)]"
                v-on:click="handleChange(option)"
                v-on:keydown.enter="handleChange(option)"
            >
                <NuxtLink
                    v-if="option"
                    :href="storefrontLink(pack.slug, option.vendor)?.toString()"
                    target="_blank"
                    :aria-label="`link to ${option.text} storefront`"
                    tabindex="0"
                    class="external-storefront item"
                    :class="getProductClass(pack.slug)"
                    external
                    v-on:click="$launchDarkly.trackStorefrontClick(pack.slug, option.vendor, product)"
                >
                    <div class="flex-container">
                        <div class="flex-row">
                            <PlatformIcon
                                v-if="option.icon"
                                :platform="option.icon"
                                :classes="['plat-icon']"
                            />
                            <div class="dropdown-text">
                                {{ option.text }}
                                <ClientOnly>
                                    <div v-if="option.price">
                                        {{ priceString(option) }}
                                    </div>
                                    <template v-slot:fallback>
                                        <div class="skeleton" />
                                    </template>
                                </ClientOnly>
                            </div>
                        </div>
                        <div>
                            <div v-if="discounted(option)" class="sale"> {{ $t('PACK_DETAIL.ON_SALE') }} </div>
                        </div>
                    </div>
                </NuxtLink>
            </div>
        </ClientOnly>
    </div>
</div>
</template>

<script lang="ts">
import type Shopify from '@jackboxgames/shopify'
import { defineComponent, type PropType } from 'vue'
import PlatformIcon from 'public/images/icons/platforms/PlatformIcon.vue'
import { getStorefrontLink, getProductGTMClass, localizedPriceString } from '$services/storefronts/storefronts'
import type { JBGWeb } from '$types/JBGWeb'

export default defineComponent({
    components: { PlatformIcon },
    props: {
        options: {
            type: Array as PropType<JBGWeb.DropdownOption[]>,
            required: true
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Select an option'
        },
        tabindex: {
            type: Number,
            required: false,
            default: 0
        },
        labelText: {
            type: String,
            required: false
        },
        error: {
            type: Boolean,
            required: false,
            default: false
        },
        icon: {
            type: String,
            required: false,
            default: null
        },
        pack: {
            type: Object as PropType<JBGWeb.PackDetail>
        },
        product: {
            type: Object as PropType<Shopify.Product>
        },
        fellBackToEn: {
            type: Boolean,
            required: false,
            default: false
        },
        isBundle: {
            type: Boolean,
            required: false,
            default: false
        },
        isLocalized: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    emits: ['dropdownChange'],

    data() {
        let selected
        if (this.placeholder) {
            selected = { text: this.placeholder, value: null }
        } else {
            selected = this.options[0]
        }

        return {
            selected,
            open: false
        }
    },

    mounted() {
        this.$emit('dropdownChange', this.selected)
    },

    methods: {
        discounted(option: JBGWeb.DropdownOption) {
            if (!this.product) return false
            if (!this.product.priceRange) return false
            const maxPrice = this.product.compareAtPriceRange.maxVariantPrice.amount

            // Don't highlight sales below 5% off
            const minSaleThreshold = 5
            const percentOff = Math.floor((1 - (+option.price / maxPrice)) * 100)

            return percentOff >= minSaleThreshold
        },

        handleChange(option: JBGWeb.DropdownOption) {
            this.selected = option
            this.open = false
            this.$emit('dropdownChange', option.text)
        },

        getProductClass(packSlug: string) {
            return getProductGTMClass(packSlug)
        },
        priceString(option: JBGWeb.DropdownOption): string | undefined {
            const p = option.price
            if (!p) {
                return undefined
            }
            return localizedPriceString(p, option.currencyCode, this.$i18n.locale)
        },
        storefrontLink(slug: string, platform: string): URL | undefined {
            if (!platform || !slug) return undefined
            if (!this.pack) return undefined
            const { locale } = useI18n()
            return getStorefrontLink(locale.value, slug, platform, this.fellBackToEn, this.isBundle, this.isLocalized)
        }
    }
})
</script>

<style lang="scss" scoped>
.custom-dropdown {
    z-index: 1;

    a {
        color: inherit;
        display: block;
        text-decoration: inherit;
        width: 100%;
    }
}
</style>
